import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  FileText,
  Loader2,
  Lock,
  MapPin,
  Pencil,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  ACCEPT_ATTR,
  ACCEPTED_DOCUMENT_TYPES,
  ID_PROOF_KINDS,
  IFSC_RE,
  INDIAN_STATES,
  MAX_DOCUMENT_BYTES,
  prepareDocumentFile,
  MOBILE_RE,
  PAN_RE,
  PIN_RE,
  STATUS_STYLE,
  formatBytes,
  formatDateTime,
  lookupIfsc,
  lookupPincode,
} from "../lib/payoutSetup";

const INPUT_CLASS = "h-11 rounded-md border-zinc-200 focus-visible:ring-black";
const SELECT_CLASS =
  "h-11 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black";
const LINK_CLASS = "text-xs font-medium text-zinc-600 underline underline-offset-2 hover:text-black";

// Only what can't be inferred. Business type, description, display name,
// account holder and GSTIN are filled in (or kept) by the server.
const EMPTY_FORM = {
  legal_business_name: "",
  phone: "",
  pan: "",
  email: "",
  postal_code: "",
  city: "",
  state: "",
  street1: "",
  account_number: "",
  confirm_account_number: "",
  ifsc: "",
  consent: false,
};

const STEP_TEXT = {
  submitted: "We have your details.",
  account_created: "We open a Razorpay account in your name using these details.",
  under_review: "Razorpay checks your documents. This can take a few working days.",
  activated: "Razorpay approves your account.",
  connected: "Your account is linked to Growithevent. Ticket payments go straight to your bank.",
};

function Field({ label, hint, error, optional, htmlFor, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-sm font-medium mb-1.5 block">
        {label}
        {optional && <span className="text-zinc-400 font-normal"> (optional)</span>}
      </Label>
      {children}
      {error ? (
        <p className="mt-1.5 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-xs text-zinc-500">{hint}</p>
      ) : null}
    </div>
  );
}

function FormSection({ title, subtitle, children }) {
  return (
    <div role="group" aria-label={title} className="border-t border-zinc-100 pt-6 first:border-0 first:pt-0">
      <p className="font-heading text-lg font-semibold tracking-tight">{title}</p>
      {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
      <div className="space-y-5 mt-4">{children}</div>
    </div>
  );
}

function SavedValue({ masked, onChange, testId }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={testId}>
      <span className="inline-flex items-center gap-1.5 h-11 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-700">
        <Lock className="w-3.5 h-3.5 text-zinc-400" />
        Saved {masked}
      </span>
      <Button type="button" variant="outline" className="h-11 rounded-md border-zinc-200" onClick={onChange}>
        Change
      </Button>
    </div>
  );
}

function StatusPill({ status, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
        STATUS_STYLE[status] || STATUS_STYLE.submitted
      }`}
      data-testid="payout-setup-status"
    >
      {label}
    </span>
  );
}

function StepIcon({ state }) {
  if (state === "done") return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
  if (state === "current") return <Clock className="w-5 h-5 text-sky-600" />;
  if (state === "attention") return <AlertTriangle className="w-5 h-5 text-amber-600" />;
  if (state === "stopped") return <XCircle className="w-5 h-5 text-red-600" />;
  return <Circle className="w-5 h-5 text-zinc-300" />;
}

function Timeline({ steps }) {
  return (
    <ol className="space-y-0" data-testid="payout-setup-timeline">
      {steps.map((step, index) => (
        <li key={step.key} className="flex gap-3">
          <div className="flex flex-col items-center">
            <StepIcon state={step.state} />
            {index < steps.length - 1 && (
              <span
                className={`w-px flex-1 my-1 ${step.state === "done" ? "bg-emerald-300" : "bg-zinc-200"}`}
              />
            )}
          </div>
          <div className="pb-5">
            <p
              className={`text-sm font-medium ${
                step.state === "upcoming" ? "text-zinc-400" : "text-zinc-900"
              }`}
            >
              {step.key === "connected" ? "Connected — payments go to your bank" : step.label}
              {step.state === "current" && (
                <span className="ml-2 text-xs font-normal text-sky-700">in progress</span>
              )}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {step.at ? formatDateTime(step.at) : STEP_TEXT[step.key]}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-zinc-100 last:border-0">
      <span className="text-zinc-500 shrink-0">{label}</span>
      <span className="text-zinc-900 text-right break-words min-w-0">{value || "—"}</span>
    </div>
  );
}

function StatusCard({ setup, onEdit }) {
  const details = setup.details || {};
  const status = setup.status;
  const note = setup.note;
  const address = [details.street1, details.street2, details.city, details.state, details.postal_code]
    .filter(Boolean)
    .join(", ");
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="payout-setup-status-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <p className="font-medium">Your payout setup request</p>
          <p className="text-sm text-zinc-500">
            Submitted {formatDateTime(setup.submitted_at)}
            {setup.resubmitted_at && ` · updated ${formatDateTime(setup.resubmitted_at)}`}
          </p>
        </div>
        <StatusPill status={status} label={setup.status_label} />
      </div>

      {status === "needs_changes" && (
        <div className="mb-5 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="payout-setup-note">
          <p className="text-sm font-medium text-amber-900 mb-1">We need a few changes</p>
          <p className="text-sm text-amber-900 whitespace-pre-line">{note}</p>
          <Button
            type="button"
            onClick={onEdit}
            className="mt-3 h-9 bg-black text-white hover:bg-zinc-800 rounded-md"
            data-testid="payout-setup-fix-button"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Update my details
          </Button>
        </div>
      )}
      {status === "rejected" && (
        <div className="mb-5 rounded-md border border-red-200 bg-red-50 p-4" data-testid="payout-setup-note">
          <p className="text-sm font-medium text-red-900 mb-1">We couldn't open your Razorpay account</p>
          <p className="text-sm text-red-900 whitespace-pre-line">{note}</p>
          <p className="text-xs text-red-800 mt-2">Reply to our email or contact Growithevent support to talk it through.</p>
        </div>
      )}
      {note && status !== "needs_changes" && status !== "rejected" && (
        <div className="mb-5 rounded-md border border-zinc-200 bg-zinc-50 p-4" data-testid="payout-setup-note">
          <p className="text-xs font-medium text-zinc-500 mb-1">Note from Growithevent</p>
          <p className="text-sm text-zinc-800 whitespace-pre-line">{note}</p>
        </div>
      )}

      <Timeline steps={setup.timeline || []} />

      <div className="mt-2 grid gap-6 sm:grid-cols-2">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">You</p>
          <SummaryRow label="Name on PAN" value={details.legal_business_name} />
          <SummaryRow label="PAN" value={details.pan_masked} />
          {details.gst_masked && <SummaryRow label="GSTIN" value={details.gst_masked} />}
          <SummaryRow label="Mobile" value={details.phone && `+91 ${details.phone}`} />
          <SummaryRow label="Address" value={address} />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">Bank account</p>
          <SummaryRow label="Account" value={details.account_number_masked} />
          <SummaryRow label="IFSC" value={details.ifsc} />
          {details.bank_name && <SummaryRow label="Bank" value={details.bank_name} />}
        </div>
      </div>

      {setup.documents?.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 mb-2">Documents</p>
          <ul className="text-sm text-zinc-700 space-y-1">
            {setup.documents.map((doc) => (
              <li key={doc.id} className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-zinc-400" />
                {doc.slot_label}
                {doc.kind_label && doc.kind_label !== doc.slot_label && ` (${doc.kind_label})`}
                <span className="text-zinc-400">
                  {setup.documents_purged ? "· deleted after activation" : `· ${doc.filename}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {setup.editable && status !== "needs_changes" && (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            className="rounded-md border-zinc-200"
            data-testid="payout-setup-edit-button"
          >
            <Pencil className="w-4 h-4 mr-1.5" /> Edit details
          </Button>
          <p className="text-xs text-zinc-500">
            You can change your details until we start creating your Razorpay account.
          </p>
        </div>
      )}
    </div>
  );
}

function UploadRow({ slot, label, note, optional, pendingDoc, committedDoc, uploading, onUpload, onRemove, error }) {
  const inputRef = useRef(null);
  const current = pendingDoc || committedDoc;
  return (
    <div className="py-3 border-b border-zinc-100 last:border-0" data-testid={`payout-doc-${slot}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {label}
            {optional && <span className="text-zinc-400 font-normal"> (optional)</span>}
          </p>
          {current ? (
            <p className="text-xs text-zinc-500 flex items-center gap-1.5 min-w-0">
              <FileText className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{current.filename}</span>
              <span className="shrink-0">· {formatBytes(current.size)}</span>
              {pendingDoc && committedDoc && <span className="shrink-0 text-amber-700">· replaces the old file</span>}
              {!pendingDoc && <span className="shrink-0 text-emerald-700">· submitted</span>}
            </p>
          ) : (
            note && <p className="text-xs text-zinc-500">{note}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {pendingDoc && (
            <button
              type="button"
              onClick={() => onRemove(pendingDoc.id)}
              className="p-2 text-zinc-400 hover:text-red-600"
              aria-label={`Remove ${label}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onUpload(file);
            }}
            data-testid={`payout-doc-input-${slot}`}
          />
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="h-9 rounded-md border-zinc-200"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-1.5" />
            )}
            {current ? "Replace" : "Upload"}
          </Button>
        </div>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function validate(form, { isEdit, changing, documentsBySlot }) {
  const errors = {};
  if (form.legal_business_name.trim().length < 2) {
    errors.legal_business_name = "Enter your full name exactly as on your PAN card.";
  }
  if (!MOBILE_RE.test(form.phone)) errors.phone = "Enter your 10-digit mobile number.";
  if ((!isEdit || changing.pan) && !PAN_RE.test(form.pan)) {
    errors.pan = "PAN is 10 characters, like ABCDE1234F.";
  }
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!PIN_RE.test(form.postal_code)) {
    errors.postal_code = "PIN code is 6 digits.";
  } else if (!form.city.trim() || !form.state) {
    errors.place = "Add your city and state.";
  }
  if (form.street1.trim().length < 3) errors.street1 = "Enter your house, street and area.";
  if (!isEdit || changing.account) {
    if (!/^[0-9]{9,18}$/.test(form.account_number)) {
      errors.account_number = "Account number is 9 to 18 digits.";
    } else if (form.account_number !== form.confirm_account_number) {
      errors.confirm_account_number = "The two account numbers don't match.";
    }
  }
  if (!IFSC_RE.test(form.ifsc)) errors.ifsc = "IFSC is 11 characters, like HDFC0001234 (5th character is zero).";
  if (!documentsBySlot.id_proof) errors.doc_id_proof = "Upload the front of your ID.";
  if (!form.consent) errors.consent = "Please tick this box to continue.";
  return errors;
}

/**
 * The organizer hands over what Growithevent needs to open a Razorpay account
 * in their name: name, mobile, PAN, address, bank and one ID. Everything
 * Razorpay asks that is the same for every organizer (business type,
 * description, display name, holder name) is filled in by the server.
 * Sensitive values are encrypted by the server and only shown back masked.
 */
export default function PayoutSetupForm({ gatewayConnected }) {
  const { user } = useAuth();
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [changing, setChanging] = useState({ pan: true, account: true });
  const [editingEmail, setEditingEmail] = useState(false);
  const [place, setPlace] = useState({ status: "idle" });
  const [editPlace, setEditPlace] = useState(false);
  const [pending, setPending] = useState([]);
  const [idKind, setIdKind] = useState("aadhaar_masked");
  const [otherId, setOtherId] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [ifscInfo, setIfscInfo] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const topRef = useRef(null);
  const lookedUpPin = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/organizer/payout-setup");
      setSetup(data);
      setPending(data.pending_documents || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!setup) return;
    const docs = [...(setup.documents || []), ...(setup.pending_documents || [])];
    const id = docs.find((d) => d.slot === "id_proof");
    if (id?.kind) {
      setIdKind(id.kind);
      setOtherId(id.kind !== "aadhaar_masked");
    }
  }, [setup]);

  useEffect(() => {
    if (setup && !setup.exists) {
      setForm((f) => ({ ...f, email: f.email || user?.email || "" }));
    }
  }, [setup, user]);

  // PIN code → city and state (India Post), so most people type neither.
  const pin = form.postal_code;
  useEffect(() => {
    if (!PIN_RE.test(pin) || pin === lookedUpPin.current) return undefined;
    let cancelled = false;
    setPlace({ status: "loading" });
    const timer = setTimeout(async () => {
      const result = await lookupPincode(pin);
      if (cancelled) return;
      lookedUpPin.current = pin;
      if (result?.city && result?.state) {
        setForm((f) => ({ ...f, city: result.city, state: result.state }));
        setPlace({ status: "found" });
        setEditPlace(false);
      } else {
        if (result?.city) setForm((f) => ({ ...f, city: result.city, state: "" }));
        setPlace({ status: "failed", notFound: Boolean(result?.notFound) });
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pin]);

  const ifsc = form.ifsc;
  useEffect(() => {
    if (!IFSC_RE.test(ifsc)) {
      setIfscInfo(null);
      return undefined;
    }
    let cancelled = false;
    setIfscInfo({ loading: true });
    const timer = setTimeout(async () => {
      const result = await lookupIfsc(ifsc);
      if (!cancelled) setIfscInfo(result ? { ...result, ifsc } : null);
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ifsc]);

  const isEdit = Boolean(setup?.exists);
  const showForm = setup && (!setup.exists || editing);

  const committedBySlot = useMemo(() => {
    if (!setup?.exists || setup.documents_purged) return {};
    return Object.fromEntries((setup.documents || []).map((d) => [d.slot, d]));
  }, [setup]);
  const pendingBySlot = useMemo(
    () => Object.fromEntries(pending.map((d) => [d.slot, d])),
    [pending]
  );
  const documentsBySlot = { ...committedBySlot, ...pendingBySlot };
  const errors = showErrors ? validate(form, { isEdit, changing, documentsBySlot }) : {};

  const set = (key, transform) => (e) => {
    const value = transform ? transform(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };
  const upper = (v) => v.toUpperCase().replace(/\s/g, "");
  const digits = (max) => (v) => v.replace(/\D/g, "").slice(0, max);

  const startEditing = () => {
    const d = setup.details || {};
    setForm({
      ...EMPTY_FORM,
      legal_business_name: d.legal_business_name || "",
      phone: d.phone || "",
      email: d.email || user?.email || "",
      postal_code: d.postal_code || "",
      city: d.city || "",
      state: d.state || "",
      // The form has one address line; older requests may have had two.
      street1: [d.street1, d.street2].filter(Boolean).join(", "),
      ifsc: d.ifsc || "",
    });
    lookedUpPin.current = d.postal_code || null;
    setPlace({ status: d.city && d.state ? "found" : "idle" });
    setEditPlace(false);
    setEditingEmail(false);
    setChanging({ pan: false, account: false });
    setShowErrors(false);
    setEditing(true);
  };

  const uploadDocument = async (slot, picked, kind) => {
    setUploading(slot);
    try {
      // Shrinks big phone photos (and converts HEIC) to a JPEG under 2 MB.
      const file = await prepareDocumentFile(picked).catch(() => picked);
      const okType =
        ACCEPTED_DOCUMENT_TYPES.includes(file.type) || /\.(jpe?g|png|pdf)$/i.test(file.name);
      if (!okType) {
        toast.error("Please choose a photo (JPG or PNG) or a PDF.");
        return;
      }
      if (file.size > MAX_DOCUMENT_BYTES) {
        toast.error("That file is bigger than 2 MB. Please upload a photo of the card instead.");
        return;
      }
      const { data } = await api.post(`/organizer/payout-setup/documents/${slot}`, file, {
        params: { kind, filename: file.name },
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      setPending(data.pending_documents || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
    }
  };

  const removeDocument = async (documentId) => {
    try {
      const { data } = await api.delete(`/organizer/payout-setup/documents/${documentId}`);
      setPending(data.pending_documents || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setShowErrors(true);
    const found = validate(form, { isEdit, changing, documentsBySlot });
    if (Object.keys(found).length) {
      if (found.place) setEditPlace(true);
      if (found.email) setEditingEmail(true);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    const ifscMatches = ifscInfo?.ifsc === form.ifsc;
    const payload = {
      legal_business_name: form.legal_business_name.trim(),
      phone: form.phone,
      email: form.email.trim(),
      postal_code: form.postal_code,
      city: form.city.trim(),
      state: form.state,
      street1: form.street1.trim(),
      street2: "",
      ifsc: form.ifsc,
      bank_name: ifscMatches ? ifscInfo.bank || "" : setup?.details?.bank_name || "",
      bank_branch:
        ifscMatches && ifscInfo.branch
          ? [ifscInfo.branch, ifscInfo.city].filter(Boolean).join(", ")
          : setup?.details?.bank_branch || "",
      consent: form.consent,
    };
    if (!isEdit || changing.pan) payload.pan = form.pan;
    if (!isEdit || changing.account) {
      payload.account_number = form.account_number;
      payload.confirm_account_number = form.confirm_account_number;
    }

    setSubmitting(true);
    try {
      const { data } = isEdit
        ? await api.put("/organizer/payout-setup", payload)
        : await api.post("/organizer/payout-setup", payload);
      setSetup(data);
      setPending(data.pending_documents || []);
      setEditing(false);
      setForm(EMPTY_FORM);
      setShowErrors(false);
      toast.success(
        isEdit
          ? "Updated. We'll pick it up from here."
          : "Thanks! We've received your details and will start opening your Razorpay account."
      );
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-zinc-500">Loading…</p>;
  if (!setup) return null;

  const manualPlace = editPlace || place.status === "failed";
  const idKindLabel = ID_PROOF_KINDS.find((k) => k.value === idKind)?.label || "ID";

  return (
    <section ref={topRef} data-testid="payout-setup-section">
      <div className="mb-5">
        <p className="overline mb-2">Payout setup</p>
        <h2 className="font-heading text-2xl font-semibold tracking-tight mb-2">
          Get ticket money straight to your bank
        </h2>
        <p className="text-zinc-500 max-w-xl text-sm leading-relaxed">
          {setup.exists
            ? "Follow your Razorpay account being set up. We email you whenever you need to do something."
            : "Takes about 2 minutes. We use these details to open a Razorpay account in your name, so attendees' payments go straight to your bank. Keep your PAN, Aadhaar and bank details handy."}
        </p>
      </div>

      {!setup.available && (
        <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="payout-setup-unavailable">
          <p className="font-medium mb-1">Payout setup isn't open yet</p>
          <p className="text-sm text-zinc-500">
            We're getting this ready. Please check back soon or contact Growithevent support.
          </p>
        </div>
      )}

      {setup.available && gatewayConnected && !setup.exists && !editing && (
        <div className="mb-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          Your own Razorpay account is already connected, so payments already go
          to you. You only need this form if you want us to open a new account.
        </div>
      )}

      {setup.available && setup.exists && !editing && (
        <StatusCard setup={setup} onEdit={startEditing} />
      )}

      {setup.available && showForm && (
        <form
          onSubmit={submit}
          noValidate
          className="bg-white border border-zinc-200 rounded-md p-6 space-y-6"
          data-testid="payout-setup-form"
        >
          <FormSection title="About you">
            <Field
              label="Full name as on PAN"
              hint="Exactly as printed on your PAN card."
              error={errors.legal_business_name}
              htmlFor="ps-legal-name"
            >
              <Input
                id="ps-legal-name"
                autoFocus
                value={form.legal_business_name}
                onChange={set("legal_business_name")}
                autoComplete="name"
                className={INPUT_CLASS}
                data-testid="ps-legal-name"
              />
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Mobile number" error={errors.phone} htmlFor="ps-phone">
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
                    +91
                  </span>
                  <Input
                    id="ps-phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.phone}
                    onChange={set("phone", digits(10))}
                    autoComplete="tel-national"
                    placeholder="10 digits"
                    className={`${INPUT_CLASS} rounded-l-none`}
                    data-testid="ps-phone"
                  />
                </div>
              </Field>
              <Field label="PAN" error={errors.pan} htmlFor="ps-pan">
                {isEdit && !changing.pan ? (
                  <SavedValue
                    masked={setup.details?.pan_masked}
                    onChange={() => setChanging((c) => ({ ...c, pan: true }))}
                    testId="ps-pan-saved"
                  />
                ) : (
                  <Input
                    id="ps-pan"
                    value={form.pan}
                    onChange={set("pan", (v) => upper(v).slice(0, 10))}
                    placeholder="ABCDE1234F"
                    autoComplete="off"
                    autoCapitalize="characters"
                    className={`${INPUT_CLASS} uppercase tracking-wider`}
                    data-testid="ps-pan"
                  />
                )}
              </Field>
            </div>
            {editingEmail ? (
              <Field label="Email for Razorpay updates" error={errors.email} htmlFor="ps-email">
                <Input
                  id="ps-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  autoComplete="email"
                  className={INPUT_CLASS}
                  data-testid="ps-email"
                />
              </Field>
            ) : (
              <p className="text-sm text-zinc-600" data-testid="ps-email-readonly">
                Razorpay updates go to <span className="font-medium text-zinc-900">{form.email}</span>{" "}
                <button type="button" className={LINK_CLASS} onClick={() => setEditingEmail(true)}>
                  change
                </button>
              </p>
            )}
          </FormSection>

          <FormSection title="Address">
            <Field label="PIN code" error={errors.postal_code || errors.place} htmlFor="ps-pin">
              <Input
                id="ps-pin"
                inputMode="numeric"
                maxLength={6}
                value={form.postal_code}
                onChange={set("postal_code", digits(6))}
                autoComplete="postal-code"
                placeholder="6 digits"
                className={`${INPUT_CLASS} max-w-[10rem]`}
                data-testid="ps-pin"
              />
              {!manualPlace && place.status === "loading" && (
                <p className="mt-1.5 text-xs text-zinc-500">Finding your city…</p>
              )}
              {!manualPlace &&
                place.status !== "loading" &&
                PIN_RE.test(form.postal_code) &&
                form.city &&
                form.state && (
                  <p className="mt-2 flex items-center gap-1.5 text-sm text-zinc-700" data-testid="ps-place">
                    <MapPin className="w-4 h-4 text-zinc-400" />
                    {form.city}, {form.state}
                    <button type="button" className={`${LINK_CLASS} ml-1`} onClick={() => setEditPlace(true)}>
                      edit
                    </button>
                  </p>
                )}
              {place.status === "failed" && !editPlace && (
                <p className="mt-1.5 text-xs text-zinc-500">
                  {place.notFound
                    ? "We couldn't find this PIN. Please check it, or type your city and state below."
                    : "Please type your city and state below."}
                </p>
              )}
            </Field>
            {manualPlace && (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="City" htmlFor="ps-city">
                  <Input
                    id="ps-city"
                    value={form.city}
                    onChange={set("city")}
                    autoComplete="address-level2"
                    className={INPUT_CLASS}
                    data-testid="ps-city"
                  />
                </Field>
                <Field label="State / UT" htmlFor="ps-state">
                  <select
                    id="ps-state"
                    value={form.state}
                    onChange={set("state")}
                    className={SELECT_CLASS}
                    data-testid="ps-state"
                  >
                    <option value="">Choose…</option>
                    {INDIAN_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
            <Field label="House, street, area" error={errors.street1} htmlFor="ps-street1">
              <Input
                id="ps-street1"
                value={form.street1}
                onChange={set("street1")}
                autoComplete="street-address"
                placeholder="e.g. 12, 4th Cross, Indiranagar"
                className={INPUT_CLASS}
                data-testid="ps-street1"
              />
            </Field>
          </FormSection>

          <FormSection
            title="Bank account"
            subtitle="Where your ticket money goes. The account must be in your own name (the name on your PAN)."
          >
            {isEdit && !changing.account ? (
              <Field label="Account number">
                <SavedValue
                  masked={setup.details?.account_number_masked}
                  onChange={() => setChanging((c) => ({ ...c, account: true }))}
                  testId="ps-account-saved"
                />
              </Field>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Account number" error={errors.account_number} htmlFor="ps-account">
                  <Input
                    id="ps-account"
                    inputMode="numeric"
                    value={form.account_number}
                    onChange={set("account_number", digits(18))}
                    autoComplete="off"
                    className={INPUT_CLASS}
                    data-testid="ps-account"
                  />
                </Field>
                <Field
                  label="Re-enter account number"
                  error={errors.confirm_account_number}
                  hint={
                    form.confirm_account_number &&
                    form.confirm_account_number === form.account_number
                      ? "✓ Matches"
                      : null
                  }
                  htmlFor="ps-account-confirm"
                >
                  <Input
                    id="ps-account-confirm"
                    inputMode="numeric"
                    value={form.confirm_account_number}
                    onChange={set("confirm_account_number", digits(18))}
                    onPaste={(e) => e.preventDefault()}
                    autoComplete="off"
                    className={INPUT_CLASS}
                    data-testid="ps-account-confirm"
                  />
                </Field>
              </div>
            )}
            <Field
              label="IFSC"
              hint={
                ifscInfo?.loading
                  ? "Looking up your branch…"
                  : ifscInfo?.notFound
                  ? "We couldn't find this IFSC. Please check it against your cheque book."
                  : ifscInfo?.bank
                  ? `${ifscInfo.bank}${ifscInfo.branch ? ` · ${ifscInfo.branch}` : ""}${
                      ifscInfo.city ? `, ${ifscInfo.city}` : ""
                    }`
                  : "11 characters, printed on your cheque book or passbook."
              }
              error={errors.ifsc}
              htmlFor="ps-ifsc"
            >
              <Input
                id="ps-ifsc"
                value={form.ifsc}
                onChange={set("ifsc", (v) => upper(v).slice(0, 11))}
                placeholder="HDFC0001234"
                autoComplete="off"
                autoCapitalize="characters"
                className={`${INPUT_CLASS} uppercase tracking-wider max-w-xs`}
                data-testid="ps-ifsc"
              />
            </Field>
          </FormSection>

          <FormSection
            title="ID proof"
            subtitle={
              idKind === "aadhaar_masked"
                ? "A clear photo of your Aadhaar card. Any phone photo works."
                : "A clear photo or a PDF."
            }
          >
            <div className="rounded-md border border-zinc-200 px-4" data-testid="ps-id-proof">
              <div className="flex items-center justify-between gap-3 py-3 border-b border-zinc-100 flex-wrap">
                {otherId ? (
                  <select
                    value={idKind}
                    onChange={(e) => setIdKind(e.target.value)}
                    className={`${SELECT_CLASS} h-9 w-auto min-w-[12rem]`}
                    aria-label="ID type"
                    data-testid="ps-id-kind"
                  >
                    {ID_PROOF_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>
                        {k.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <>
                    <p className="text-sm font-medium">{idKindLabel}</p>
                    <button
                      type="button"
                      className={LINK_CLASS}
                      onClick={() => setOtherId(true)}
                      data-testid="ps-other-id"
                    >
                      Use a different ID
                    </button>
                  </>
                )}
              </div>
              <UploadRow
                slot="id_proof"
                label="Front"
                note="All four corners visible."
                pendingDoc={pendingBySlot.id_proof}
                committedDoc={committedBySlot.id_proof}
                uploading={uploading === "id_proof"}
                onUpload={(file) => uploadDocument("id_proof", file, idKind)}
                onRemove={removeDocument}
                error={errors.doc_id_proof}
              />
              <UploadRow
                slot="id_proof_back"
                label="Back"
                optional
                note="Skip if your PDF already has both sides."
                pendingDoc={pendingBySlot.id_proof_back}
                committedDoc={committedBySlot.id_proof_back}
                uploading={uploading === "id_proof_back"}
                onUpload={(file) => uploadDocument("id_proof_back", file, idKind)}
                onRemove={removeDocument}
              />
              <UploadRow
                slot="pan_card"
                label="PAN card photo"
                optional
                note="Not required: Razorpay checks your PAN number. Adding it can speed things up."
                pendingDoc={pendingBySlot.pan_card}
                committedDoc={committedBySlot.pan_card}
                uploading={uploading === "pan_card"}
                onUpload={(file) => uploadDocument("pan_card", file, "pan_card")}
                onRemove={removeDocument}
              />
            </div>
          </FormSection>

          <div className="border-t border-zinc-100 pt-6">
            <label className="flex items-start gap-3 cursor-pointer" htmlFor="ps-consent">
              <Checkbox
                id="ps-consent"
                checked={form.consent}
                onCheckedChange={(checked) => setForm((f) => ({ ...f, consent: checked === true }))}
                className="mt-0.5"
                data-testid="ps-consent"
              />
              <span className="text-sm text-zinc-700 leading-relaxed">{setup.consent_text}</span>
            </label>
            {errors.consent && <p className="mt-1.5 text-xs text-red-600">{errors.consent}</p>}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="submit"
              disabled={submitting || Boolean(uploading)}
              className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-6"
              data-testid="payout-setup-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : isEdit ? (
                "Save and resubmit"
              ) : (
                "Submit my details"
              )}
            </Button>
            {isEdit && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditing(false);
                  setForm(EMPTY_FORM);
                  setShowErrors(false);
                }}
                className="h-11 rounded-md border-zinc-200"
              >
                Cancel
              </Button>
            )}
          </div>
          <p className="text-xs text-zinc-400">
            Your PAN, account number and documents are encrypted. Only the
            Growithevent founder can see them, to open your Razorpay account.
          </p>
        </form>
      )}
    </section>
  );
}
